// API Configuration
// Can be overridden via REACT_APP_API_BASE_URL environment variable
export const API_CONFIG = {
  // Base URL for all API endpoints
  // Defaults to /api (relative path) - proxy handles forwarding in dev, web server in prod
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  
  // API version
  version: 'v1',
  
  // Request timeout in milliseconds
  timeout: 10000,
  
  // Default headers for all requests
  defaultHeaders: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  
  // Endpoints configuration
  endpoints: {
    // Authentication endpoints
    auth: {
      login: '/auth/login',
      logout: '/auth/logout',
      refresh: '/auth/refresh',
      me: '/auth/me',
      changePassword: '/auth/change-password',
      forgotPassword: '/auth/forgot-password',
      resetPassword: '/auth/reset-password',
      verifyEmail: '/auth/verify-email',
      resendVerification: '/auth/resend-verification',
    },
    
    // Dashboard endpoints
    dashboard: {
      stats: '/dashboard/stats',
      monthlyData: '/dashboard/monthly-data',
      recentActivity: '/dashboard/recent-activity',
      kpis: '/admin/kpis',
    },
    
    // Requests endpoints
    requests: {
      list: '/requests',
      create: '/requests',
      get: '/requests/:id',
      update: '/requests/:id',
      delete: '/requests/:id',
      status: '/requests/:id/status',
      allRequests: '/service-users/all-requests',
    },
    
    // Payments endpoints
    payments: {
      list: '/payments',
      get: '/payments/:id',
      update: '/payments/:id',
      refund: '/payments/:id/refund',
      transactions: '/payments/transactions',
    },
    
    // Service Providers (Mechanics) endpoints
    serviceProviders: {
      list: '/service-providers',
      create: '/service-providers',
      get: '/service-providers/:id',
      update: '/service-providers/:id',
      delete: '/service-providers/:id',
      approve: '/service-providers/:id/approve',
      reject: '/service-providers/:id/reject',
      block: '/service-providers/:id/block',
      unblock: '/service-providers/:id/unblock',
      status: '/service-providers/status',
      pending: '/service-providers/pending-payment-signups',
    },
    
    // Service Users (Drivers) endpoints
    serviceUsers: {
      list: '/service-users',
      get: '/service-users/:id',
      update: '/service-users/:id',
      delete: '/service-users/:id',
      block: '/service-users/:id/block',
      unblock: '/service-users/:id/unblock',
    },
    
    // Notifications endpoints
    notifications: {
      list: '/notifications',
      get: '/notifications/:id',
      markAsRead: '/notifications/:id/read',
      markAllAsRead: '/notifications/read-all',
      create: '/notifications',
      delete: '/notifications/:id',
    },
    
    // User Management endpoints
    users: {
      list: '/users',
      create: '/users',
      get: '/users/:id',
      update: '/users/:id',
      delete: '/users/:id',
      roles: '/users/roles',
      permissions: '/users/permissions',
    },
    
    // Settings endpoints
    settings: {
      get: '/settings',
      update: '/settings',
      system: '/settings/system',
      notifications: '/settings/notifications',
    },
    
    // Master Data endpoints
    masterData: {
      serviceProviderTypes: '/master-data/service-provider-types',
      categories: '/master-data/categories/:serviceProviderTypeTag',
      manage: '/master-data/manage',
    },
  },
};

// Helper function to build full API URL
export const buildApiUrl = (endpoint: string, params?: Record<string, string | number>): string => {
  let url = `${API_CONFIG.baseURL}${endpoint}`;
  
  // Replace URL parameters
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, String(value));
    });
  }
  
  return url;
};

// Helper function to get endpoint URL
export const getEndpoint = (path: string): string => {
  const keys = path.split('.');
  let endpoint: any = API_CONFIG.endpoints;
  
  for (const key of keys) {
    endpoint = endpoint[key];
    if (!endpoint) {
      throw new Error(`Endpoint not found: ${path}`);
    }
  }
  
  return endpoint;
};

// Environment-specific configurations
export const getApiConfig = () => {
  const env = process.env.NODE_ENV;
  
  switch (env) {
    case 'development':
      return {
        ...API_CONFIG,
        // In development, use /api - setupProxy.js handles proxying to avoid CORS
        baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
        timeout: 15000, // Longer timeout for development
      };
    case 'production':
      return {
        ...API_CONFIG,
        // In production, use /api - Apache/web server proxy handles forwarding
        // Can be overridden via REACT_APP_API_BASE_URL environment variable
        baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
        timeout: 10000,
        // Production headers - no CORS headers needed as they're set by the server
        defaultHeaders: {
          ...API_CONFIG.defaultHeaders,
        },
      };
    case 'test':
      return {
        ...API_CONFIG,
        baseURL: 'https://api-dev.uniprime.io',
        timeout: 5000,
      };
    default:
      return API_CONFIG;
  }
};
